import { FolderGit2, GitMerge, Plus, Rocket, Save, Trash2, Undo2 } from '@orgus/ui/icons';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { ENVIRONMENT_KINDS, ENVIRONMENT_KIND_LABEL, STACK_EXT, suggestedVerifyCommand } from '@orgus/protocol';
import type { EnvironmentKind, LandingProjectConfig, LandingProjectConfigInput, StackState } from '@orgus/protocol';
import { Badge, Button, EmptyState, ErrorState, IconButton, Input, Panel, RelativeTime, Segmented, Select, SkeletonRows, Tooltip, toUiError, toast } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { useOrgus, useProjects } from '../../lib/store.ts';
import { projectConfig, saveConfig, useLanding } from './api.ts';
import { sortEnvironments } from './model.ts';

interface EnvDraft {
  key: number;
  name: string;
  kind: EnvironmentKind;
  autoDeployFrom: string;
  deployCommand: string;
  rollbackCommand: string;
  protected: boolean;
}

interface Draft {
  verifyCommand: string;
  verifyTimeoutMin: string;
  deployCommand: string;
  machineLabels: string;
  protectedBranches: string;
  environments: EnvDraft[];
}

let envKey = 0;

function toDraft(c: LandingProjectConfig): Draft {
  return {
    verifyCommand: c.verifyCommand,
    verifyTimeoutMin: String(c.verifyTimeoutMin),
    deployCommand: c.deployCommand,
    machineLabels: c.machineLabels.join(', '),
    protectedBranches: c.protectedBranches.join(', '),
    environments: sortEnvironments(c.environments).map((e) => ({
      key: ++envKey,
      name: e.name,
      kind: e.kind,
      autoDeployFrom: e.autoDeployFrom ?? '',
      deployCommand: e.deployCommand ?? '',
      rollbackCommand: e.rollbackCommand ?? '',
      protected: e.protected,
    })),
  };
}

const list = (s: string) =>
  s
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);

function toInput(d: Draft): LandingProjectConfigInput {
  return {
    verifyCommand: d.verifyCommand,
    verifyTimeoutMin: Number(d.verifyTimeoutMin),
    deployCommand: d.deployCommand,
    machineLabels: list(d.machineLabels),
    protectedBranches: list(d.protectedBranches),
    environments: d.environments.map((e) => ({
      name: e.name.trim().toLowerCase(),
      kind: e.kind,
      autoDeployFrom: e.autoDeployFrom.trim() || null,
      ...(e.deployCommand.trim() ? { deployCommand: e.deployCommand.trim() } : {}),
      ...(e.rollbackCommand.trim() ? { rollbackCommand: e.rollbackCommand.trim() } : {}),
      protected: e.kind === 'production' ? true : e.protected,
    })),
  };
}

/** Settings, Landing queue: per project, how the queue verifies and deploys, and its environments. */
export function LandingSettings() {
  const load = useLanding();
  const projects = useProjects();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const projectId = params.get('project') && projects.some((p) => p.id === params.get('project')) ? params.get('project')! : (projects[0]?.id ?? '');
  const project = projects.find((p) => p.id === projectId);
  const saved = load.state?.configs.find((c) => c.projectId === projectId);
  const base = useMemo(() => (load.state && project ? projectConfig(load.state, project.id, project.defaultBranch) : null), [load.state, project]);
  // What the repo itself says verifies it (stack detection, ADR 0046): offered, never saved by itself.
  const stack = useOrgus((s) => (s.ext as Record<string, unknown> | undefined)?.[STACK_EXT] as StackState | undefined);
  const suggestion = suggestedVerifyCommand(stack?.profiles[projectId]?.profile);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);

  // A new project, or settings saved elsewhere, reset the form.
  useEffect(() => {
    if (base) setDraft(toDraft(base));
    setError(null);
  }, [projectId, base?.updatedAt, !!base]);

  if (load.phase === 'loading') {
    return (
      <div className="rounded-card border border-border bg-surface" role="status" aria-label="Loading landing settings">
        <SkeletonRows rows={6} />
      </div>
    );
  }
  if (load.phase === 'error') {
    return (
      <div className="rounded-card border border-border bg-surface">
        <ErrorState error={load.error} onRetry={load.retry} />
      </div>
    );
  }
  if (!projects.length || !project || !base || !draft) {
    return (
      <div className="rounded-card border border-border bg-surface">
        <EmptyState
          icon={<FolderGit2 />}
          title="No projects yet"
          description="Add a project on a machine to set up its queue and environments."
          action={
            <Button variant="secondary" icon={<FolderGit2 />} onClick={() => navigate('/projects')}>
              Go to Projects
            </Button>
          }
        />
      </div>
    );
  }

  const patch = (p: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...p } : d));
  const patchEnv = (key: number, p: Partial<EnvDraft>) => setDraft((d) => (d ? { ...d, environments: d.environments.map((e) => (e.key === key ? { ...e, ...p } : e)) } : d));
  const dirty = JSON.stringify(toInput(draft)) !== JSON.stringify(toInput(toDraft(base)));
  const timeout = Number(draft.verifyTimeoutMin);
  const timeoutError = !Number.isFinite(timeout) || timeout < 1 || timeout > 720 ? 'Between 1 and 720 minutes.' : undefined;
  const names = draft.environments.map((e) => e.name.trim().toLowerCase());
  const nameError = (name: string, i: number) => (!/^[a-z0-9][a-z0-9-]{0,39}$/.test(name.trim().toLowerCase()) ? 'Lower case letters, digits and dashes.' : names.indexOf(name.trim().toLowerCase()) !== i ? 'Already used.' : undefined);
  const invalid = !!timeoutError || draft.environments.some((e, i) => !!nameError(e.name, i));
  // Offered while the field is empty, or still holds Orgus's detected command (ADR 0066) and the
  // repo has a narrower one (test:affected). Use this only fills the form; a person saves.
  const verify = draft.verifyCommand.trim();
  const offer = !!suggestion && verify !== suggestion.command && (!verify || (!saved && !!base.detected && draft.verifyCommand === base.verifyCommand));

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const next = await saveConfig(project.id, toInput(draft), base);
      setDraft(toDraft(next));
      toast.success('Landing settings saved', project.name);
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="flex flex-col gap-6"
      onSubmit={(e) => {
        e.preventDefault();
        if (!invalid && dirty) void save();
      }}
    >
      <div className="flex flex-wrap items-end gap-3">
        <Select
          label="Project"
          wrapperClassName="w-72"
          value={project.id}
          options={projects.map((p) => ({ value: p.id, label: p.name }))}
          onValueChange={(v) =>
            setParams(
              (p) => {
                const n = new URLSearchParams(p);
                n.set('project', v);
                return n;
              },
              { replace: true },
            )
          }
        />
        <p className="flex h-control items-center text-caption text-fg-subtle">
          {saved ? (
            <>
              Saved by {saved.updatedBy} <RelativeTime ts={saved.updatedAt} />
            </>
          ) : (
            <Badge tone={base?.detected ? 'neutral' : 'waiting'}>{base?.detected ? 'Set up by Orgus from the repository' : 'Not set up: these are the defaults'}</Badge>
          )}
        </p>
      </div>

      <Panel title="Verify" icon={<GitMerge />} bodyClassName="grid grid-cols-1 gap-4 p-4 md:grid-cols-3">
        <Input
          wrapperClassName="md:col-span-3"
          label="Verify command"
          value={draft.verifyCommand}
          onChange={(e) => patch({ verifyCommand: e.target.value })}
          placeholder="pnpm typecheck && pnpm test"
          className="font-mono"
          spellCheck={false}
          hint={
            !saved && base?.detected
              ? `Detected: ${base.detected.evidence}. Runs in a fresh checkout of the change rebased onto the target; exit code 0 lands it.`
              : 'Runs in a fresh checkout of the change rebased onto the target. Exit code 0 lands it. Empty: landings park until it is set.'
          }
        />
        {offer && suggestion && (
          <div className="flex min-w-0 flex-wrap items-center gap-2 md:col-span-3" aria-label="Suggested verify command">
            <span className="text-caption text-fg-subtle">Suggested from {suggestion.evidence}</span>
            <Tooltip content={suggestion.runs ? `Runs: ${suggestion.runs}` : suggestion.command}>
              <code tabIndex={0} className="max-w-full truncate rounded-control bg-sunken px-2 py-1 font-mono text-caption text-fg">
                {suggestion.command}
              </code>
            </Tooltip>
            <Button size="sm" variant="secondary" onClick={() => patch({ verifyCommand: suggestion.command })}>
              Use this
            </Button>
          </div>
        )}
        <Input label="Timeout" type="number" min={1} max={720} value={draft.verifyTimeoutMin} onChange={(e) => patch({ verifyTimeoutMin: e.target.value })} hint="Minutes." error={timeoutError} />
        <Input
          label="Machine labels"
          value={draft.machineLabels}
          onChange={(e) => patch({ machineLabels: e.target.value })}
          placeholder="ci, linux"
          spellCheck={false}
          hint="Only machines with every label verify and deploy; empty means any online machine with a checkout."
        />
        <Input
          label="Protected branches"
          value={draft.protectedBranches}
          onChange={(e) => patch({ protectedBranches: e.target.value })}
          placeholder={project.defaultBranch}
          spellCheck={false}
          hint={`Only the queue pushes here; * matches anything, empty means ${project.defaultBranch} and queue targets.`}
        />
      </Panel>

      <Panel title="Deploy" icon={<Rocket />} bodyClassName="flex flex-col gap-4 p-4">
        <Input
          label="Deploy command"
          value={draft.deployCommand}
          onChange={(e) => patch({ deployCommand: e.target.value })}
          placeholder="pnpm deploy --env {env} --version {version}"
          className="font-mono"
          spellCheck={false}
          hint="Takes {env}, {version}, {sha} and {branch}; empty means nothing deploys."
        />
        <div className="flex flex-col gap-3">
          <div className="flex items-center">
            <h3 className="text-ui font-medium text-fg">Environments</h3>
            <Button
              size="sm"
              variant="ghost"
              icon={<Plus />}
              className="ml-auto"
              disabled={draft.environments.length >= 12}
              onClick={() => patch({ environments: [...draft.environments, { key: ++envKey, name: '', kind: 'preview', autoDeployFrom: '', deployCommand: '', rollbackCommand: '', protected: false }] })}
            >
              Add environment
            </Button>
          </div>
          {draft.environments.length === 0 && <p className="text-ui text-fg-subtle">No environments: landings stop at the push.</p>}
          {draft.environments.map((e, i) => (
            <fieldset key={e.key} className="grid grid-cols-1 gap-3 rounded-card border border-border-subtle p-3 md:grid-cols-4">
              <legend className="sr-only">Environment {e.name || i + 1}</legend>
              <Input label="Name" size="sm" value={e.name} onChange={(ev) => patchEnv(e.key, { name: ev.target.value })} placeholder="staging" spellCheck={false} error={nameError(e.name, i)} />
              <Select
                label="Kind"
                size="sm"
                value={e.kind}
                options={ENVIRONMENT_KINDS.map((k) => ({ value: k, label: ENVIRONMENT_KIND_LABEL[k] }))}
                onValueChange={(v) => patchEnv(e.key, { kind: v as EnvironmentKind, ...(v === 'production' ? { protected: true } : {}) })}
              />
              <Input label="Follows branch" size="sm" value={e.autoDeployFrom} onChange={(ev) => patchEnv(e.key, { autoDeployFrom: ev.target.value })} placeholder="Manual only" spellCheck={false} hint="Landings on it deploy here." />
              <div className="flex items-end">
                <IconButton label={`Remove ${e.name || 'this environment'}`} icon={<Trash2 />} onClick={() => patch({ environments: draft.environments.filter((x) => x.key !== e.key) })} />
              </div>
              <Input label="Deploy command" size="sm" value={e.deployCommand} onChange={(ev) => patchEnv(e.key, { deployCommand: ev.target.value })} placeholder="Project's command" className="font-mono" spellCheck={false} wrapperClassName="md:col-span-2" />
              <Input label="Rollback command" size="sm" value={e.rollbackCommand} onChange={(ev) => patchEnv(e.key, { rollbackCommand: ev.target.value })} placeholder="Deploy command" className="font-mono" spellCheck={false} />
              <div className="flex flex-col gap-2">
                <span className="text-ui font-medium text-fg-muted">Changes by</span>
                <Segmented
                  label={`Who can change ${e.name || 'this environment'}`}
                  value={e.kind === 'production' || e.protected ? 'protected' : 'open'}
                  onChange={(v) => e.kind !== 'production' && patchEnv(e.key, { protected: v === 'protected' })}
                  options={[
                    { value: 'open', label: 'Operators', disabled: e.kind === 'production' },
                    { value: 'protected', label: 'Admins' },
                  ]}
                />
              </div>
            </fieldset>
          ))}
          <p className="text-sm text-fg-subtle">Production is always protected: an admin deploys it by typing the exact version.</p>
        </div>
      </Panel>

      {error && <ErrorState variant="inline" error={error} />}
      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" icon={<Undo2 />} disabled={!dirty || busy} onClick={() => setDraft(toDraft(base))}>
          Discard changes
        </Button>
        <Button type="submit" variant="primary" icon={<Save />} loading={busy} disabled={!dirty || invalid}>
          Save settings
        </Button>
      </div>
    </form>
  );
}
